import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Input
} from '@mui/material'
import { ChangeEvent, useContext, useState } from 'react'
import { useNotify } from 'react-admin'
import { useDuplicateModelMutation } from '../../../apollo/configurator/mutations/DuplicateModel.generated'
import { RootNodesDocument } from '../../../apollo/configurator/queries/RootNodes.generated'
import { ErrorContext } from '../../../context/error/ErrorContext'
import { ModelContext } from '../../../context/model/ModelContext'

interface DuplicateModelDialogProps {
    open: boolean
    modelId: string
    onClose: () => void
}

export const DuplicateModelDialog: React.FC<DuplicateModelDialogProps> = ({
    open,
    modelId,
    onClose
}) => {
    const [inputValue, setInputValue] = useState('')
    const [inputError, setInputError] = useState(true)

    const notify = useNotify()
    const { setState } = useContext(ModelContext)
    const { setErrorState } = useContext(ErrorContext)

    const [duplicateModel, { loading: duplicating }] = useDuplicateModelMutation({
        refetchQueries: [{ query: RootNodesDocument }],
        onCompleted(data) {
            if (data.configuratorDuplicateModel) {
                notify('Model has been duplicated successfully', { type: 'success' })
                // Reset model state
                setState({
                    currentModel: '',
                    currentModelCode: '',
                    hasChanges: false,
                    hasEdited: false,
                    updatedModel: false,
                    showEditedMessage: false
                })
            }
            handleClose()
        },
        onError(error) {
            setErrorState({ hasError: true, message: error.message })
            notify('Model has not been duplicated successfully', { type: 'error' })
        }
    })

    const handleClose = () => {
        setInputValue('')
        setInputError(true)
        onClose()
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInputValue(value)
        setInputError(value.trim() === '')
    }

    const handleConfirm = () => {
        if (!inputError && inputValue.trim()) {
            duplicateModel({
                variables: {
                    source: +modelId as any,
                    destination: inputValue.trim()
                }
            })
        }
    }
    return (
        <Dialog
            open={open}
            keepMounted
            onClose={handleClose}
            aria-labelledby="duplicate-dialog-title"
            aria-describedby="duplicate-dialog-description"
        >
            <DialogTitle id="duplicate-dialog-title">Duplicate Model</DialogTitle>
            <DialogContent>
                <DialogContentText id="duplicate-dialog-description">
                    Please enter a name for the duplicated model:
                </DialogContentText>
                <Input
                    autoFocus
                    fullWidth
                    margin="dense"
                    placeholder="Model name"
                    value={inputValue}
                    onChange={handleInputChange}
                    error={inputError}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="contained">
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={inputError || duplicating}
                >
                    {duplicating ? 'Duplicating...' : 'Duplicate'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
